import { IPagedListResponse, PagedListResponse } from "domain/types/global/pagedListResponse";
import { PagedListRequest } from "domain/types/global/pagedListRequest";
import { ThunkAction } from 'redux-thunk';
import { RootStateBase } from "../store/baseStore";
import { Action } from 'redux';

/**
 * Interface para gerenciar estado temporário de dados paginados durante operações de filtragem
 * @template T Tipo do item de dados
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export interface PagedTemp<T = any> {
	/** Nome da propriedade no estado onde os dados paginados estão armazenados */
	PropName: string | number | symbol;

	/** Nome da ação de dispatch associada a estes dados paginados */
	DispatchName: string;

	/** Objeto de estado completo contendo os dados paginados */
	/* eslint-disable @typescript-eslint/no-explicit-any */
	Payload: Record<string | number | symbol, {
		Result: T[];
		Filtered?: any;
		TotalCount?: number;
		Count?: number;
		AsDataTables?: any;
		Reset?: boolean;
		[key: string]: any;
	}>;

	/** Propriedade chave usada para identificar de forma única itens no array Result */
	UniqueKey: keyof T;
}

/**
 * Tipo para ações assíncronas (thunks) na aplicação
 * @template ReturnType Tipo de retorno da ação assíncrona
 */
export type AppThunk<ReturnType = void> = ThunkAction<
	ReturnType, // Tipo de retorno
	RootStateBase,  // Tipo do estado global
	undefined,  // Argumentos extras (ou outro tipo, se houver)
	Action      // Tipo de ação básica
>;

/**
 * Cria uma ação para atualizar dados paginados no estado
 * @template T Tipo do item de dados na requisição
 * @param dispatchName Nome da ação de dispatch
 * @param request Objeto de requisição de lista paginada
 * @param response Objeto de resposta de lista paginada
 * @returns Objeto de ação para o reducer
 */
export function DispatchPaged<T>(dispatchName: string, request: PagedListRequest<T>, response: PagedListResponse<any>) {
    response.Filtered = request.filter
    response.Take = request.take
    response.Skip = request.skip
    response.Reset = false;
    return {
        payload: response,
        type: dispatchName
    }
}

export function DispatchPagedReset<T>(dispatchName: string, request: PagedListRequest<T>, take : number) {
    const response =  {} as  PagedListResponse<any>
    response.Filtered = request.filter
    response.Take = take
    response.Skip = 0
    response.Result = []
    response.Reset = true;
    return {
        payload: response,
        type: dispatchName + "_RESET"
    }
}

export function DispatchReset(dispatchName : string) : string {
    return dispatchName + "_RESET";
}

/**
 * Reducer para resetar dados paginados no estado
 * @template T Tipo do objeto de estado
 * @template K Tipo da chave da propriedade no estado que armazena dados paginados
 * @template R Tipo que contém um array Result
 * @template P Tipo da chave usada para identificar itens únicos no array Result
 * @param state Estado atual
 * @param propName Nome da propriedade no estado que armazena dados paginados
 * @param payload Dados da resposta paginada
 * @param uniqueKey Chave usada para identificar itens únicos no array Result
 * @param dispatchName Nome da ação de dispatch
 * @returns Novo estado com dados paginados resetados
 */
export function PagedReducerReset<T, K extends keyof T, R extends { Result: any[] }, P extends keyof R["Result"][number]>(
	state: T, propName: K, payload: IPagedListResponse<R["Result"][number]>, uniqueKey: P, dispatchName: string): T {

	// Reseta o estado temporário relacionado à dispatchName
	ResetPagedTemp(dispatchName);

	// Retorna um novo estado com a propriedade específica (propName) resetada
	return {
		...state,
		[propName]: {
			...state[propName],
			Result: [], // Limpa o array de resultados
			TotalCount: 0, // Reseta o contador total
			Count: 0, // Reseta o contador
			AsDataTables: undefined, // Reseta a propriedade AsDataTables, se houver
			Reset: true
		}
	};
}

/**
 * Reducer principal para manipular dados paginados no estado
 * @template T Tipo do objeto de estado
 * @template K Tipo da chave da propriedade no estado que armazena dados paginados
 * @template R Tipo que contém um array Result
 * @template P Tipo da chave usada para identificar itens únicos no array Result
 * @param state Estado atual
 * @param propName Nome da propriedade no estado que armazena dados paginados
 * @param payload Dados da resposta paginada
 * @param uniqueKey Chave usada para identificar itens únicos no array Result
 * @param dispatchName Nome da ação de dispatch
 * @returns Novo estado com dados paginados atualizados
 */
export function PagedReducer<T, K extends keyof T, R extends { Result: any[] }, P extends keyof R["Result"][number]>(
    state: T, propName: K, payload: IPagedListResponse<R["Result"][number]>, uniqueKey: P, dispatchName: string): T {
        const hasFilter = payload.Filtered?.some(filter => filter.Values?.length > 0) ?? false;

        function zeraResult () {
            state = {
                ...state,
                [propName]: {
                    ...state[propName],
                    Result: []
                }
            };
        }

        
        if(hasFilter){
            const y = {
                ...state,
                [propName]:payload
            };
            zeraResult();
            ResetPagedTemp(dispatchName);
            return y
        }else{
            if(payload.Skip == 0){
                zeraResult()
            }
        }
        

    //var currentState = {...state}


	const currentProp = state[propName] as unknown as R | undefined;


    if (hasFilter) {

        const stateTemp = TempPaged.find(f => f.DispatchName == dispatchName);
        if (stateTemp == null) {
            TempPaged = [...TempPaged, { Payload: state, DispatchName: dispatchName, PropName: propName, UniqueKey: uniqueKey } as PagedTemp]
            zeraResult()

        } else {
            if (stateTemp.Payload[propName].Filtered !== payload.Filtered) {
                zeraResult()
            }
        }

        SavePagedTemp(dispatchName, payload)

        const y = {
            ...state,
            [propName]: {
                ...currentProp,
                Result:
                    [
                        ...(currentProp?.Result ?? []),
                        ...payload.Result.filter(
                            newUser => !(currentProp?.Result ?? []).some(
                                (existingUser) =>
                                    existingUser[uniqueKey] === newUser[uniqueKey]

                            )
                        )
                    ],
                TotalCount: payload.TotalCount,
                Count: payload.Count,
                //AsDataTables: payload.AsDataTables,
                Filtered: payload.Filtered,
                Reset: false
            }
        };
        return y
    } else {
        const stateTemp = TempPaged.find(f => f.DispatchName == dispatchName);
        const t = stateTemp && stateTemp.Payload[propName as keyof typeof stateTemp.Payload]?.Result
            ? stateTemp.Payload[propName as keyof typeof stateTemp.Payload].Result
            : [];

        if (stateTemp != null) {
            TempPaged = TempPaged.filter((el) => { return el.DispatchName != dispatchName; });

        }

        return {
            ...state,
            [propName]: {
                ...currentProp,
                Result: [
                    ...(
                        t.length > 0 ?
                            currentProp?.Result ?
                                [
                                    ...t,
                                    ...currentProp.Result.filter(item2 => !t.some(item1 => item1[uniqueKey] === item2[uniqueKey])),
                                ]
                                : []
                            : currentProp?.Result ?? []),
                    ...payload.Result.filter(
                        (newItem) =>
                            !(currentProp?.Result ?? []).some(
                                (existingItem) => existingItem[uniqueKey] === newItem[uniqueKey]
                            )
                    )
                ],
                TotalCount: payload.TotalCount,
                Count: payload.Count,
                //AsDataTables: payload.AsDataTables,
                Filtered: payload.Filtered,
                Reset: false
            }
        };
    }
}




















// export function PagedReducer<T, K extends keyof T, R extends { Result: any[] }, P extends keyof R["Result"][number]>(
//     state: T, propName: K, payload: IPagedListResponse<R["Result"][number]>, uniqueKey: P, dispatchName: string): T {
//         const hasFilter = payload.Filtered?.some(filter => filter.Values?.length > 0) ?? false;

//         const zeraResult = () => {
//             state = {
//                 ...state,
//                 [propName]: {
//                     ...state[propName],
//                     Result: []
//                 }
//             };
//         };
      

//     //var currentState = {...state}


//     const currentProp = state[propName] as unknown as R | undefined;


//     if (hasFilter) {

//         const stateTemp = TempPaged.find(f => f.DispatchName == dispatchName);
//         if (stateTemp == null) {
//             TempPaged = [...TempPaged, { Payload: state, DispatchName: dispatchName, PropName: propName, UniqueKey: uniqueKey } as PagedTemp]
//             zeraResult()

//         } else {
//             //@ts-expect-error : Funciona
//             if (stateTemp.Payload[propName].Filtered !== payload.Filtered) {
//                 zeraResult()
//             }
//         }

//         SavePagedTemp(dispatchName, payload)

//         const y = {
//             ...state,
//             [propName]: {
//                 ...currentProp,
//                 Result:
//                     [
//                         ...(currentProp?.Result ?? []),
//                         ...payload.Result.filter(
//                             newUser => !(currentProp?.Result ?? []).some(
//                                 (existingUser) =>
//                                     existingUser[uniqueKey] === newUser[uniqueKey]

//                             )
//                         )
//                     ],
//                 TotalCount: payload.TotalCount,
//                 Count: payload.Count,
//                 AsDataTables: payload.AsDataTables,
//                 Filtered: payload.Filtered,
//                 Reset: false
//             }
//         };
//         return y
//     } else {
//         const stateTemp = TempPaged.find(f => f.DispatchName == dispatchName);
//         const t = stateTemp && stateTemp.Payload[propName as keyof typeof stateTemp.Payload]?.Result
//             ? stateTemp.Payload[propName as keyof typeof stateTemp.Payload].Result
//             : [];

//         if (stateTemp != null) {
//             TempPaged = TempPaged.filter(function (el) { return el.DispatchName != dispatchName; });

//         }

//         return {
//             ...state,
//             [propName]: {
//                 ...currentProp,
//                 Result: [
//                     ...(
//                         t.length > 0 ?
//                             currentProp?.Result ?
//                                 [
//                                     ...t,
//                                     //@ts-expect-error: Funciona
//                                     ...currentProp.Result.filter(item2 => !t.some(item1 => item1[uniqueKey] === item2[uniqueKey])),
//                                 ]
//                                 : []
//                             : currentProp?.Result ?? []),
//                     ...payload.Result.filter(
//                         (newItem) =>
//                             !(currentProp?.Result ?? []).some(
//                                 (existingItem) => existingItem[uniqueKey] === newItem[uniqueKey]
//                             )
//                     )
//                 ],
//                 TotalCount: payload.TotalCount,
//                 Count: payload.Count,
//                 AsDataTables: payload.AsDataTables,
//                 Filtered: payload.Filtered,
//                 Reset: false
//             }
//         };
//     }
// }












//Comentado por falta de uso
// function mergeResults<T>(
//     newItems: T[],
//     existingItems: T[],
//     uniqueKey: keyof T
// ): T[] {

//     const filteredNewItems = newItems.filter(
//         (newItem) =>
//             !existingItems.some(
//                 (existingItem) => existingItem[uniqueKey] === newItem[uniqueKey]
//             )
//     );

//     const filteredExistingItems = existingItems.filter(
//         (existingItem) =>
//             !newItems.some(
//                 (newItem) => newItem[uniqueKey] === existingItem[uniqueKey]
//             )
//     );

//     return [...filteredNewItems, ...filteredExistingItems];
// }

let TempPaged: PagedTemp[] = []


function ResetPagedTemp(dispatchName: string) {
	TempPaged = TempPaged.filter(item => item.DispatchName !== dispatchName);
}

/**
 * Salva ou atualiza o estado temporário de dados paginados para uma ação de dispatch específica
 * @template T Tipo do item de resultado
 * @template R Tipo com array Result
 * @param DispatchName Nome da ação de dispatch
 * @param Payload Dados da resposta de lista paginada
 */
function SavePagedTemp<T, R extends { Result: T[] }>(
	DispatchName: string,
	Payload: IPagedListResponse<R["Result"][number]>
) {
	// Atualiza o estado temporário para a ação de dispatch específica
	const updatedTempPaged = TempPaged.map((item) => {
		if (item.DispatchName === DispatchName) {
			const currentProp = item.Payload;
			const uniqueKey = item.UniqueKey;
			const dynamicPropName = item.PropName;

			// Obtém os dados atuais da propriedade
			const currentPropData = currentProp[dynamicPropName as keyof typeof currentProp];

			// Cria dados de propriedade atualizados com resultados mesclados
			const updatedDynamicProp = {
				...currentPropData,
				Filtered: Payload.Filtered,
				Result: [
					...(currentPropData?.Result ?? []),
					...Payload.Result.filter(
						(newItem) =>
							!(currentPropData?.Result ?? []).some(
								(existingItem) =>
									existingItem[uniqueKey] === newItem[uniqueKey as keyof typeof newItem]
							)
					),
				],
			};

			// Retorna item atualizado
			return {
				...item,
				Payload: {
					...currentProp,
					[dynamicPropName]: updatedDynamicProp,
				},
			};
		}
		return item;
	});

	// Atualiza o array global com os estados temporários atualizados
	TempPaged = updatedTempPaged as PagedTemp[];
}